import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardActionArea from '@mui/material/CardActionArea';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import Grid from '@mui/material/Grid';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import LocalOfferOutlinedIcon from '@mui/icons-material/LocalOfferOutlined';
import TrendingUpOutlinedIcon from '@mui/icons-material/TrendingUpOutlined';
import CardGiftcardOutlinedIcon from '@mui/icons-material/CardGiftcardOutlined';
import ListAltOutlinedIcon from '@mui/icons-material/ListAltOutlined';
import ReplayOutlinedIcon from '@mui/icons-material/ReplayOutlined';
import ConfirmationNumberOutlinedIcon from '@mui/icons-material/ConfirmationNumberOutlined';
import LeaderboardOutlinedIcon from '@mui/icons-material/LeaderboardOutlined';
import SavingsOutlinedIcon from '@mui/icons-material/SavingsOutlined';
import LayersOutlinedIcon from '@mui/icons-material/LayersOutlined';
import type { SvgIconComponent } from '@mui/icons-material';
import type { TemplateDef } from '../types';

const templateIcons: Record<string, SvgIconComponent> = {
  t1: LocalOfferOutlinedIcon,
  t2: TrendingUpOutlinedIcon,
  t3: CardGiftcardOutlinedIcon,
  t4a: ListAltOutlinedIcon,
  t4b: ReplayOutlinedIcon,
  t5: ConfirmationNumberOutlinedIcon,
  t6: LeaderboardOutlinedIcon,
  t7: SavingsOutlinedIcon,
  t8: LayersOutlinedIcon,
};

interface TemplateSelectorProps {
  templates: TemplateDef[];
  selectedId: string | null;
  onSelect: (id: string) => void;
}

export default function TemplateSelector({ templates, selectedId, onSelect }: TemplateSelectorProps) {
  return (
    <Box>
      <Typography variant="h6" fontWeight={700} gutterBottom sx={{ mb: 0.5 }}>
        Selecione o Template
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Escolha o tipo de mecânica promocional para carregar os campos correspondentes.
      </Typography>
      <Grid container spacing={2}>
        {templates.map((tmpl) => {
          const Icon = templateIcons[tmpl.id] ?? LocalOfferOutlinedIcon;
          const isSelected = selectedId === tmpl.id;
          return (
            <Grid key={tmpl.id} size={{ xs: 12, sm: 6, md: 4 }}>
              <Card
                elevation={isSelected ? 3 : 0}
                sx={{
                  border: '2px solid',
                  borderColor: isSelected ? tmpl.color : 'divider',
                  borderRadius: 2,
                  transition: 'all 0.2s',
                  height: '100%',
                  bgcolor: isSelected ? `${tmpl.color}08` : 'background.paper',
                  '&:hover': {
                    borderColor: tmpl.color,
                    transform: 'translateY(-2px)',
                    boxShadow: 3,
                  },
                }}
              >
                <CardActionArea onClick={() => onSelect(tmpl.id)} sx={{ height: '100%', alignItems: 'flex-start', p: 0 }}>
                  <CardContent sx={{ p: 2.5, height: '100%', display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
                      <Box
                        sx={{
                          width: 40,
                          height: 40,
                          borderRadius: 1.5,
                          bgcolor: isSelected ? tmpl.color : `${tmpl.color}15`,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0,
                          transition: 'background 0.2s',
                        }}
                      >
                        <Icon sx={{ fontSize: 22, color: isSelected ? '#fff' : tmpl.color }} />
                      </Box>
                      {isSelected && (
                        <CheckCircleIcon sx={{ color: tmpl.color, fontSize: 20 }} />
                      )}
                    </Box>
                    <Box>
                      <Typography
                        variant="caption"
                        sx={{ color: tmpl.color, fontWeight: 700, letterSpacing: '0.5px', textTransform: 'uppercase', fontSize: '0.65rem' }}
                      >
                        {tmpl.number}
                      </Typography>
                      <Typography variant="subtitle2" fontWeight={700} sx={{ lineHeight: 1.3, mt: 0.25 }}>
                        {tmpl.name}
                      </Typography>
                    </Box>
                    <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.4, flexGrow: 1 }}>
                      {tmpl.description}
                    </Typography>
                    <Chip
                      label={tmpl.mechanicsBadge}
                      size="small"
                      sx={{
                        height: 20,
                        fontSize: '0.6rem',
                        fontWeight: 600,
                        bgcolor: `${tmpl.color}12`,
                        color: tmpl.color,
                        border: 'none',
                        alignSelf: 'flex-start',
                        letterSpacing: '0.3px',
                      }}
                    />
                  </CardContent>
                </CardActionArea>
              </Card>
            </Grid>
          );
        })}
      </Grid>
    </Box>
  );
}
